import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BarChart3, ArrowRight, Youtube } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { useAuth } from '@/hooks/useAuth';
import { analytics } from '@/utils/analytics';
import { metaPixel } from '@/utils/meta-pixel';

export const YouTubePage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Track YouTube landing page visit
  useEffect(() => {
    analytics.trackEvent({
      action: 'view',
      category: 'page_view',
      label: 'youtube_landing',
      custom_parameters: {
        source: 'youtube',
        page_type: 'landing'
      }
    });

    // Track with Meta Pixel
    metaPixel.trackEvent('ViewContent', {
      content_name: 'YouTube Landing Page',
      content_category: 'landing',
      custom_data: {
        source: 'youtube'
      }
    });
  }, []);

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleCreateAccount = () => {
    // Track conversion from YouTube
    analytics.trackEvent({
      action: 'click',
      category: 'engagement',
      label: 'youtube_to_signup',
      custom_parameters: {
        source: 'youtube',
        conversion_type: 'signup_intent'
      }
    });

    // Track with Meta Pixel
    metaPixel.trackEvent('Lead', {
      content_name: 'YouTube to Signup',
      content_category: 'conversion',
      custom_data: {
        source: 'youtube'
      }
    });

    // Redirect to signup page specifically
    navigate('/auth?mode=signup&source=youtube&redirect=/pricing');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <Helmet>
        <title>Bienvenue depuis YouTube - Créez votre compte DPEWizard</title>
        <meta name="description" content="Créez votre compte DPEWizard et commencez à analyser vos DPE avec notre IA experte. Accès direct depuis YouTube." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="max-w-lg w-full">
        <Card className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header avec YouTube branding */}
          <div className="bg-red-600 text-white text-center py-8">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Youtube className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold mb-2">
              Bienvenue depuis YouTube !
            </h1>
            <p className="text-white/90 text-sm">
              Analysez votre DPE avec notre IA experte
            </p>
          </div>

          <CardContent className="p-8">
            {/* Message sur l'offre terminée */}
            <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mb-6 rounded">
              <p className="text-blue-900 text-sm font-medium">
                ℹ️ Les 50 crédits gratuits de l'offre YouTube ont été attribués
              </p>
            </div>

            {/* Message principal */}
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                Analysez votre DPE maintenant
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Notre IA détecte les erreurs que les diagnostiqueurs ratent et vous aide à optimiser 
                votre DPE pour valoriser votre bien immobilier.
              </p>
            </div>

            {/* Avantages simplifiés */}
            <div className="space-y-3 mb-8">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-bold text-sm">✓</span>
                </div>
                <span className="text-gray-800 font-medium">Analyse en 2 minutes</span>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-bold text-sm">✓</span>
                </div>
                <span className="text-gray-800 font-medium">28 règles expertes appliquées</span>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-bold text-sm">✓</span>
                </div>
                <span className="text-gray-800 font-medium">Plan de travaux personnalisé</span>
              </div>
            </div>

            {/* CTA principal */}
            <Button
              onClick={handleCreateAccount}
              className="w-full bg-red-600 hover:bg-red-700 text-white py-4 text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              size="lg"
            >
              Créer mon compte et lancer mon diagnostic
              <ArrowRight className="w-6 h-6 ml-3" />
            </Button>

            {/* Note de confiance */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                Première analyse à partir de 9,90€
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default YouTubePage;